// pdfjs-dist text extraction, page by page, preserving reading order.
//
// Two products come out of one pass: plain text for the section and citation
// scanners, and positional line/cell geometry for the table reconstructor.
// Everything runs in the browser; the file never leaves the machine.

import * as pdfjs from 'pdfjs-dist';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';

import type { IngestProgress } from '../../types';
import { buildLines, pageTextOf } from './layout';
import type { PageLayout } from './layout';

export { buildLines, findRunningLines, isRunningLine, pageTextOf } from './layout';
export type { PageLayout, TextCell, TextLine, PositionedItem } from './layout';

// Vite resolves this to a hashed asset URL and bundles the worker locally, so
// no CDN is contacted at runtime.
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = PdfWorker;

export interface ExtractedPdf {
  pageCount: number;
  pages: PageLayout[];
  rawText: string;
}

export async function extractPdf(
  data: ArrayBuffer,
  onProgress?: (p: IngestProgress) => void,
): Promise<ExtractedPdf> {
  const task = pdfjs.getDocument({
    data: new Uint8Array(data),
    // No network fetches for fonts or maps; extraction is text-only.
    isEvalSupported: false,
  });
  const pdf = await task.promise;
  const pageCount = pdf.numPages;
  const pages: PageLayout[] = [];

  for (let n = 1; n <= pageCount; n++) {
    onProgress?.({
      phase: 'extracting',
      page: n,
      pageCount,
      message: `Reading page ${n} of ${pageCount}`,
    });

    const page = await pdf.getPage(n);
    const content = await page.getTextContent();
    const lines = buildLines(content.items as TextItem[]);
    pages.push({ number: n, lines, text: pageTextOf(lines) });
    page.cleanup();
  }

  await pdf.destroy();

  return {
    pageCount,
    pages,
    rawText: pages.map((p) => p.text).join('\n\n'),
  };
}
